import { cn } from '@/lib/utils/cn';

/**
 * Styles des boutons, séparés du composant.
 *
 * Fichier dédié pour deux raisons. La première tient au Fast Refresh de Vite :
 * il ne conserve l'état d'un module que si celui-ci n'exporte que des
 * composants — mélanger constantes et composants provoque un rechargement
 * complet à chaque modification. C'est déjà la raison d'être de `badge-tones`.
 *
 * La seconde est qu'un **lien** doit parfois ressembler à un bouton : « Nouveau
 * besoin » navigue, donc c'est un `<a>` — ouvrable dans un nouvel onglet, avec
 * une vraie adresse — mais il doit se présenter comme l'action primaire de la
 * page. `buttonClasses()` lui donne cette apparence sans recopier la charte.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * `primary` est le seul aplat violet de l'interface, conformément à la charte :
 * « le violet doit attirer l'attention, pas dominer ». Une vue ne doit donc
 * porter qu'un bouton primaire — les actions secondaires prennent `secondary`
 * ou `ghost`.
 */
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border border-accent-800/30 bg-accent-700 bg-linear-to-b from-white/8 to-transparent text-white ' +
    'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.14),0_2px_4px_-1px_rgb(76_29_149/0.22)] ' +
    'enabled:hover:bg-accent-800 enabled:hover:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.16),0_5px_12px_-4px_rgb(76_29_149/0.3)] ' +
    'enabled:active:shadow-[inset_0_1px_3px_0_rgb(0_0_0/0.12)]',
  secondary:
    'border border-ink-200 bg-white text-navy-900 shadow-xs enabled:hover:border-ink-300 enabled:hover:bg-ink-100/70',
  ghost: 'text-ink-700 enabled:hover:bg-ink-100',
};

// Rayons alignés sur ceux des champs : 10 px à la taille `lg`, qui est celle
// des formulaires. Un bouton plus arrondi que le champ juste au-dessus se voit
// immédiatement.
export const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-lg',
  md: 'h-11 px-4 text-sm rounded-[10px]',
  // Hauteur fluide, alignée sur celle des champs de saisie.
  lg: 'h-[clamp(2.75rem,5.4vh,3.125rem)] px-5 text-[15px] rounded-[10px]',
};

/** Mise en forme commune, quel que soit l'élément qui la porte. */
export const BUTTON_BASE =
  'group relative inline-flex cursor-pointer items-center justify-center gap-2 font-semibold ' +
  'transition-[background-color,border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none ' +
  'focus-visible:ring-accent-700/30 focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:outline-none';

/**
 * Apparence d'un bouton, pour un élément qui n'en est pas un.
 *
 * ⚠️ Les variantes emploient `enabled:hover:` — un préfixe qui ne s'applique
 * qu'aux éléments de formulaire. Sur un `<a>`, ces règles ne prennent jamais :
 * le lien garde donc son état de repos au survol. C'est acceptable pour une
 * action de navigation, et préférable à dupliquer toute la charte en deux
 * jeux de classes qui divergeraient.
 */
export const buttonClasses = (
  options: { variant?: ButtonVariant; size?: ButtonSize } = {},
): string => {
  const { variant = 'primary', size = 'md' } = options;

  return cn(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size]);
};
